import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowRight, Sparkles, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Services', path: '/services' },
  { name: 'Mobile Units', path: '/mobile-vans' },
  { name: 'Impact', path: '/statistics' },
  { name: 'Contact', path: '/contact' },
  { name: 'HIPAA', path: '/hipaa' },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <>
      {/* Announcement Banner */}
      <motion.div 
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500 text-black py-2 px-4 text-center text-sm font-medium relative overflow-hidden"
        data-testid="announcement-banner"
      >
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=%2220%22 height=%2220%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cpath d=%22M0 0h20v20H0z%22 fill=%22none%22/%3E%3Cpath d=%22M10 0v20M0 10h20%22 stroke=%22rgba(0,0,0,0.05)%22 stroke-width=%221%22/%3E%3C/svg%3E')]" />
        <div className="relative flex items-center justify-center gap-2">
          <Sparkles className="w-4 h-4" />
          <span>
            <strong>Notice:</strong> We've moved! New main office in Richardson, TX. Welcome to our new website at <strong>healthchecksolutions.online</strong>
          </span>
          <Sparkles className="w-4 h-4" />
        </div>
      </motion.div>

      <nav
        data-testid="navbar"
        className={`sticky top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-zinc-950/90 backdrop-blur-xl border-b border-white/10 shadow-lg shadow-black/20'
            : 'bg-zinc-950/50 backdrop-blur-md'
        }`}
      >
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
              <motion.div
                whileHover={{ rotate: 8, scale: 1.05 }}
                transition={{ type: 'spring', stiffness: 300 }}
                className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/30"
                data-testid="logo-mark"
              >
                <ShieldCheck className="w-5 h-5 text-black" strokeWidth={2.5} />
              </motion.div>
              <div className="flex items-center gap-1 leading-none">
                <motion.span
                  className="font-bold text-xl text-white tracking-tighter font-['Outfit']"
                  whileHover={{ scale: 1.02 }}
                  data-testid="brand-name"
                >
                  HealthCheck
                </motion.span>
                <span className="font-light text-xl text-zinc-400 tracking-tight font-['Outfit'] group-hover:text-emerald-400 transition-colors">
                  Systems
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                  className="relative px-4 py-2 group"
                >
                  <span className={`text-sm font-medium transition-colors relative z-10 ${
                    location.pathname === link.path
                      ? 'text-emerald-400'
                      : 'text-zinc-400 group-hover:text-white'
                  }`}>
                    {link.name}
                  </span>
                  
                  {/* Active indicator */}
                  {location.pathname === link.path && (
                    <motion.div
                      layoutId="navbar-indicator"
                      className="absolute inset-0 bg-emerald-500/10 rounded-lg border border-emerald-500/20"
                      transition={{ type: "spring", bounce: 0.25, duration: 0.5 }}
                    />
                  )}
                  
                  {/* Hover effect */}
                  <motion.div
                    className="absolute inset-0 bg-white/5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                    whileHover={{ scale: 1.05 }}
                  />
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <Link to="/request-screening" data-testid="request-screening-nav-btn">
                <motion.div
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button className="rounded-full px-6 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-black font-medium transition-all duration-200 shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40">
                    Get Started
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </motion.div>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              data-testid="mobile-menu-btn"
              className="lg:hidden p-2 text-zinc-400 hover:text-white transition-colors"
              onClick={() => setIsOpen(!isOpen)}
              whileTap={{ scale: 0.95 }}
            >
              <AnimatePresence mode="wait">
                {isOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X className="w-6 h-6" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu className="w-6 h-6" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="lg:hidden bg-zinc-950/98 backdrop-blur-xl border-t border-white/10 overflow-hidden"
              data-testid="mobile-menu"
            >
              <div className="container mx-auto px-6 py-6 flex flex-col gap-2">
                {navLinks.map((link, index) => (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link
                      to={link.path}
                      data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                      className={`text-base font-medium py-3 px-4 rounded-xl flex items-center justify-between transition-all ${
                        location.pathname === link.path
                          ? 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/20'
                          : 'text-zinc-400 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      {link.name}
                      {location.pathname === link.path && (
                        <div className="w-2 h-2 rounded-full bg-emerald-400" />
                      )}
                    </Link>
                  </motion.div>
                ))}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <Link to="/request-screening" className="mt-4 block" data-testid="mobile-request-screening-btn">
                    <Button className="w-full rounded-full py-3 bg-emerald-500 hover:bg-emerald-400 text-black font-medium">
                      Get Started
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
};
